import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import {
  App,
  Avatar,
  Breadcrumb,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Flex,
  Row,
  Space,
  Tag,
  Typography,
  theme,
  Spin,
  Alert,
} from 'antd'
import {
  ArrowLeftOutlined,
  CreditCardOutlined,
  SettingOutlined,
  PlusOutlined,
  StopOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
  TeamOutlined,
  BarChartOutlined,
} from '@ant-design/icons'
import api from '../../services/api'
import BuyLicense from '../../components/buyLicense/BuyLicense'

const { Title, Text } = Typography
const { useToken } = theme

const SubscriptionDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { token } = useToken()
  const { message } = App.useApp()
  
  const [license, setLicense] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [buyLicenseOpen, setBuyLicenseOpen] = useState(false)

  // Ana ürün bilgilerini çek
  useEffect(() => {
    const fetchLicense = async () => {
      try {
        setLoading(true)
        const response = await api.get(`/licenses/${id}`)
        const licenseData = response.data?.data?.license || response.data?.license || response.data
        setLicense(licenseData)
      } catch (err) {
        console.error('License fetch error:', err)
        setError('Abonelik bilgileri yüklenemedi')
      } finally {
        setLoading(false)
      }
    }

    if (id) {
      fetchLicense()
    }
  }, [id])

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: token.colorBgLayout, padding: 24 }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <Spin size="large" style={{ display: 'block', textAlign: 'center', marginTop: 100 }} />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: token.colorBgLayout, padding: 24 }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <Alert
            message="Hata"
            description={error}
            type="error"
            showIcon
            action={
              <Button size="small" onClick={() => navigate('/subscriptions')}>
                Geri Dön
              </Button>
            }
          />
        </div>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', background: token.colorBgLayout, padding: 24 }}>
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <Breadcrumb
            items={[
              { title: 'Ana sayfa' },
              { title: 'Abonelikler', onClick: () => navigate('/subscriptions') },
              { title: license?.LicenseProductName || 'Abonelik Detayı' },
            ]}
            style={{ marginBottom: 8 }}
          />
          
          <Flex align="center" justify="space-between" wrap="wrap" gap={16}>
            <Flex align="center" gap={16}>
              <Button 
                icon={<ArrowLeftOutlined />} 
                onClick={() => navigate('/subscriptions')}
                type="text"
              >
                Geri Dön
              </Button>
              <Avatar 
                size={48} 
                src={license?.img} 
                icon={<CreditCardOutlined />}
                style={{ background: token.colorPrimary }}
              />
              <div>
                <Title level={3} style={{ margin: 0 }}>
                  {license?.LicenseProductName || 'Abonelik'}
                </Title>
                <Text type="secondary">
                  {license?.LicenseProductIsMonthly ? 'Aylık' : 'Tek Sefer'} • €{license?.LicenseProductPrice || 0}
                </Text>
              </div>
            </Flex>
            
            <Space>
              <Tag color={license?.IsActive ? 'success' : 'default'}>
                {license?.IsActive ? 'Aktif' : 'İptal'}
              </Tag>
            </Space>
          </Flex>
        </div>

        {/* Management Cards */}
        <Row gutter={[16, 16]}>
          {/* Alt Ürün Ekleme */}
          <Col xs={24} sm={12} lg={8}>
            <Card
              hoverable
              className="management-card"
              style={{ height: '100%', borderRadius: 12 }}
              bodyStyle={{ padding: 20 }}
            >
              <Flex align="center" gap={12} style={{ marginBottom: 12 }}>
                <div style={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: 8, 
                  background: '#e6f7ff', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <PlusOutlined style={{ color: '#1890ff', fontSize: 18 }} />
                </div>
                <div>
                  <Title level={5} style={{ margin: 0 }}>Alt Ürün Ekle</Title>
                  <Text type="secondary" style={{ fontSize: 12 }}>Yeni alt ürün satın al</Text>
                </div>
              </Flex>
              <Text type="secondary" style={{ fontSize: 13, marginBottom: 16, display: 'block' }}>
                Bu aboneliğe yeni alt ürünler ekleyebilirsiniz.
              </Text>
              <Button 
                type="primary" 
                block 
                icon={<PlusOutlined />}
                onClick={() => setBuyLicenseOpen(true)}
              >
                Alt Ürün Ekle
              </Button>
            </Card>
          </Col>

          {/* Abonelik Yönetimi */}
          <Col xs={24} sm={12} lg={8}>
            <Card
              hoverable
              className="management-card"
              style={{ height: '100%', borderRadius: 12 }}
              bodyStyle={{ padding: 20 }}
            >
              <Flex align="center" gap={12} style={{ marginBottom: 12 }}>
                <div style={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: 8, 
                  background: '#f6ffed', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <SettingOutlined style={{ color: '#52c41a', fontSize: 18 }} />
                </div>
                <div>
                  <Title level={5} style={{ margin: 0 }}>Abonelik Ayarları</Title>
                  <Text type="secondary" style={{ fontSize: 12 }}>Abonelik bilgilerini yönet</Text>
                </div>
              </Flex>
              <Text type="secondary" style={{ fontSize: 13, marginBottom: 16, display: 'block' }}>
                Abonelik detaylarını görüntüleyin ve düzenleyin.
              </Text>
              <Button block icon={<SettingOutlined />}>
                Ayarları Görüntüle
              </Button>
            </Card>
          </Col>

          {/* Fatura Bilgileri */}
          <Col xs={24} sm={12} lg={8}>
            <Card
              hoverable
              className="management-card"
              style={{ height: '100%', borderRadius: 12 }}
              bodyStyle={{ padding: 20 }}
            >
              <Flex align="center" gap={12} style={{ marginBottom: 12 }}>
                <div style={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: 8, 
                  background: '#fff7e6', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <FileTextOutlined style={{ color: '#fa8c16', fontSize: 18 }} />
                </div>
                <div>
                  <Title level={5} style={{ margin: 0 }}>Fatura Bilgileri</Title>
                  <Text type="secondary" style={{ fontSize: 12 }}>Fatura geçmişi ve ayarları</Text>
                </div>
              </Flex>
              <Text type="secondary" style={{ fontSize: 13, marginBottom: 16, display: 'block' }}>
                Fatura geçmişinizi görüntüleyin ve fatura ayarlarını yönetin.
              </Text>
              <Button block icon={<FileTextOutlined />}>
                Faturaları Görüntüle
              </Button>
            </Card>
          </Col>

          {/* Alt Ürünler Listesi */}
          <Col xs={24} lg={12}>
            <Card
              title={
                <Flex align="center" gap={8}>
                  <TeamOutlined />
                  Alt Ürünler
                </Flex>
              }
              style={{ borderRadius: 12 }}
              extra={<Tag>{license?.subProducts?.length || 0} ürün</Tag>}
            >
              {license?.subProducts?.length > 0 ? (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {license.subProducts.map((subProduct, index) => (
                    <div
                      key={index}
                      style={{
                        padding: 12,
                        border: '1px solid #f0f0f0',
                        borderRadius: 8,
                        background: '#fafafa'
                      }}
                    >
                      <Flex justify="space-between" align="center">
                        <div>
                          <Text strong>{subProduct.LicenseProductName}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {subProduct.LicenseProductIsMonthly ? 'Aylık' : 'Tek Sefer'}
                            {subProduct.LicenseProductIsRequired && ' • Zorunlu'}
                            {!subProduct.LicenseProductIsPurchasable && ' • Satın Alınamaz'}
                          </Text>
                        </div>
                        <Space>
                          <Tag color={subProduct.IsActive ? 'success' : 'default'}>
                            {subProduct.IsActive ? 'Aktif' : 'İptal'}
                          </Tag>
                          <Text strong>€{subProduct.LicenseProductPrice}</Text>
                        </Space>
                      </Flex>
                    </div>
                  ))}
                </Space>
              ) : (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <TeamOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                  <Text type="secondary">Henüz alt ürün bulunmuyor</Text>
                </div>
              )}
            </Card>
          </Col>

          {/* İstatistikler */}
          <Col xs={24} lg={12}>
            <Card
              title={
                <Flex align="center" gap={8}>
                  <BarChartOutlined />
                  İstatistikler
                </Flex>
              }
              style={{ borderRadius: 12 }}
            >
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <div style={{ textAlign: 'center', padding: 16 }}>
                    <Title level={2} style={{ margin: 0, color: token.colorPrimary }}>
                      €{license?.LicenseProductPrice || 0}
                    </Title>
                    <Text type="secondary">Ana Ürün Ücreti</Text>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ textAlign: 'center', padding: 16 }}>
                    <Title level={2} style={{ margin: 0, color: token.colorSuccess }}>
                      {license?.subProducts?.length || 0}
                    </Title>
                    <Text type="secondary">Alt Ürün Sayısı</Text>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ textAlign: 'center', padding: 16 }}>
                    <Title level={2} style={{ margin: 0, color: token.colorWarning }}>
                      €{(license?.LicenseProductPrice || 0) + (license?.subProducts?.reduce((sum, sub) => sum + (sub.LicenseProductPrice || 0), 0) || 0)}
                    </Title>
                    <Text type="secondary">Toplam Ücret</Text>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ textAlign: 'center', padding: 16 }}>
                    <Title level={2} style={{ margin: 0, color: token.colorInfo }}>
                      {license?.LicenseProductIsMonthly ? 'Aylık' : 'Tek Sefer'}
                    </Title>
                    <Text type="secondary">Ödeme Tipi</Text>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        {/* BuyLicense Modal */}
        <BuyLicense
          mainLicense={license}
          open={buyLicenseOpen}
          onCancel={() => setBuyLicenseOpen(false)}
          onSubmit={(values) => {
            setBuyLicenseOpen(false)
            message.success('Alt ürün başarıyla eklendi!')
            // License bilgilerini yeniden yükle
            window.location.reload()
          }}
        />
      </div>
    </div>
    )
}

export default SubscriptionDetail